import { useState, useEffect } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/pt-br";
import { ptBR } from "@mui/x-date-pickers/locales";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";

dayjs.extend(customParseFormat);

const FORMAT = "DD/MM/YYYY";
const PLACEHOLDER = "DD/MM/AAAA";

function formatDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  return dayjs(date).format(FORMAT);
}

function parseDate(str) {
  if (!str || typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  const parsed = dayjs(trimmed, FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
}

/** Formata enquanto digita: só dígitos, insere barras em DD/MM/AAAA */
function formatDateWhileTyping(str) {
  const digits = (str || "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

function GenericDatePicker(props) {
  const {
    label,
    value,
    onChange,
    placeholder,
    required,
    error,
    helperText,
    clearable = false,
    onClear,
    ...rest
  } = props;

  const [inputStr, setInputStr] = useState(() => formatDate(value));
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const formatted = formatDate(value);
    setInputStr(formatted);
  }, [value]);

  const handleTextFieldChange = (e) => {
    const next = formatDateWhileTyping(e.target.value);
    setInputStr(next);
    const parsed = parseDate(next);
    if (parsed) onChange(parsed);
    else if (next === "") onChange(null);
  };

  const handleBlur = () => {
    const parsed = parseDate(inputStr);
    if (parsed) {
      setInputStr(formatDate(parsed));
      onChange(parsed);
    }
  };

  const handleCalendarSelect = (dayJSdate) => {
    if (!dayJSdate || !dayJSdate.isValid()) return;
    const dateVal = dayJSdate.toDate();
    onChange(dateVal);
    setInputStr(formatDate(dateVal));
    setAnchorEl(null);
  };

  const openPopover = (e) => {
    e?.preventDefault?.();
    setAnchorEl(e?.currentTarget ?? null);
  };

  const closePopover = () => setAnchorEl(null);

  const currentDateForCalendar = value ? dayjs(value) : null;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="pt-br"
      localeText={
        ptBR.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <TextField
        label={`${label || ""}${required ? " *" : ""}`}
        value={inputStr}
        onChange={handleTextFieldChange}
        onBlur={handleBlur}
        placeholder={placeholder ?? PLACEHOLDER}
        error={!!error}
        helperText={helperText}
        size="small"
        fullWidth
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        inputProps={{
          maxLength: 10,
          "aria-label": label || "Data",
          "data-skip-guards": "true",
        }}
        sx={{ "& .MuiInputBase-input": { paddingRight: "8px" }, ...rest.sx }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {clearable && value ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClear) onClear();
                    else onChange(null);
                    setInputStr("");
                  }}
                  edge="end"
                  aria-label="Limpar data"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={openPopover}
                  edge="end"
                  aria-label="Abrir calendário"
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          value={currentDateForCalendar}
          onChange={handleCalendarSelect}
        />
      </Popover>
    </LocalizationProvider>
  );
}

export default GenericDatePicker;
